import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DatePipe } from '@angular/common';
import { EquipmentManagenementService } from '../../../../services/equipmentmanagement.service';
import { ToastrService } from 'ngx-toastr';
import { TextAreaDialogComponent } from '../../../utils/textareadialog/textarea-dialog.component';


@Component({
  selector: 'app-releasenotesdialog',
  templateUrl: './releasenotesdialog.component.html',
  styleUrls: ['./releasenotesdialog.component.less'],
  standalone: false
})
export class ReleaseNotesDialogComponent implements OnInit {
    editForm!: FormGroup;
    selectedLanguageId: number = -1;
    filteredLanguages: Language[] = [];
    originalFormValue!: any;
    fileContent: string = '';
    isFileChanged: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: {
        releaseNotesForm: FormGroup,
        isNew: boolean,
        availableLanguages: Language[],
        softwareReleaseId: number,
        softwareType: string,
        metadataLabel: string
    },
        public datePipe: DatePipe
        , private emtService: EquipmentManagenementService
        , private fb: FormBuilder
        , private dialogRef: MatDialogRef<ReleaseNotesDialogComponent>
        , @Inject('BASE_URL') private baseUrl: string
        , private toastrService: ToastrService
        , private dialogForm: MatDialog) { }

    ngOnInit(): void {
        if (this.data.isNew) {
            // New release notes: create a new form group with default values
            this.originalFormValue = {
                file: null,
                fileName: '',
                language: ''
            };
            this.editForm = this.fb.group({
                ...this.originalFormValue,
            });
        } else {
            // Existing release notes: clone the form value into a new FormGroup
            this.originalFormValue = this.data.releaseNotesForm;
            this.editForm = this.fb.group({
                ...this.originalFormValue,
            });

            this.filteredLanguages = this.data.availableLanguages;
            this.selectedLanguageId = this.data.availableLanguages.find(l => l.LanguageCultureName.toLowerCase() === this.editForm.get("language")?.value.toLowerCase())?.LanguageId ?? -1;

            this.emtService.getSoftwareReleaseNotesContent(this.data.softwareType, this.editForm.value.softwareReleaseNotesId)
                .subscribe({
                    next: (res: EMTReleaseNotesContent) => {
                        if (res?.success) {
                            this.fileContent = res.content;
                        }                      
                    },
                    error: (error) => {

                    },
                });
        }       
  }


    onFileChange(event: any) {
        const files = (event?.target as HTMLInputElement)?.files;
        if (files && files.length) {
            const file = files[0];
            this.editForm.get('file')?.setValue(file);
            this.editForm.get('fileName')?.setValue(file.name);
            this.isFileChanged = true;
        } else {
            this.editForm.get('file')?.setValue(null);
        }
    }

    onLanguageFilter(event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.filterLanguages(value.toLowerCase());
    }

    filterLanguages(value: string) {

        // If value is empty, user cleared the input
        if (!value) {
            this.selectedLanguageId = -1;
            return;
        }
       
        this.filteredLanguages = this.data.availableLanguages.filter(l =>
            l.Name.toLowerCase().includes(value.toLowerCase())
        );

        // Check if value matches any language
        const found = this.filteredLanguages.some(
            lang => this.getLanguageText(lang.LanguageId).toLowerCase() === value.toLowerCase()
        );
        if (!found) {
            this.selectedLanguageId = -1;
            return;
        }
    }

    onLanguageSelected(event: MatAutocompleteSelectedEvent) {
        const selectedId = event.option.value;
        this.editForm.patchValue({ language: this.data.availableLanguages.find(l => l.LanguageId === selectedId)?.LanguageCultureName });
        this.selectedLanguageId = selectedId;
    }

    onLanguageFormFieldClick() {
        this.selectedLanguageId = -1;
        this.filteredLanguages = this.data.availableLanguages; // Reset filter
    }

    getLanguageText(languageId: number) {
        let lang = this.data.availableLanguages.find(l => l.LanguageId === languageId);
        if (lang)
            return lang.Name;
        else
            return "";
    }

    anyChange(): boolean {
        return this.pendingChanges();
    }

    pendingChanges(): boolean {
        return this.diffValues()
            && this.selectedLanguageId !== -1
            && (this.data.isNew ? this.editForm.value.file != null : true);
    }

    diffValues(): boolean {
        if (this.data.isNew) {
            return true;
        }
        const current = this.editForm.value;
        const original = this.originalFormValue;

        return current.fileName !== original.fileName;
    } 
    
    onEditFileContent() {
        const dialogRef = this.dialogForm.open(TextAreaDialogComponent, {
            height: '450px',
            width: '650px',
            data: {
                title: `Edit ${this.editForm.value.fileName}`,
                value: this.fileContent
            },
            panelClass: 'details-dialog-panel',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                let body: EMTSoftwareReleaseNotesFileChangesRequest = {
                    releaseNotesId: this.editForm.value.softwareReleaseNotesId,
                    fileContent: result,
                    softwareType: this.data.softwareType
                }
                this.emtService.updateReleaseNotesContent(body)
                    .subscribe({
                        next: (res: UpsertResponse) => {
                            if (!res?.error) {
                                this.toastrService.success('File content successfully saved!');
                                this.editForm.value.changedByUser = res.changedByUser;
                                this.fileContent = result;
                            }
                            else {
                                this.toastrService.error('File content could not be saved.');
                            }

                        },
                        error: (error) => {
                            this.toastrService.error('File content could not be saved.');
                        },
                    });
            }           
        });
    }

    onSave() {
        if (this.anyChange()) {

            let formData: FormData = new FormData();

            if (this.editForm.value.file && this.editForm.value.fileName) {
                formData.append('file', this.editForm.value.file);
            }

            let releaseNotesData: EMTUpsertReleaseNotesRequest = {
                language: this.editForm.value.language,
                softwareReleaseId: this.data.softwareReleaseId,
                softwareType: this.data.softwareType,
                softwareReleaseNotesId: this.editForm.value.softwareReleaseNotesId 
            }
            formData.append('releaseNotesData', JSON.stringify(releaseNotesData));

            this.emtService.upsertReleaseNotes(formData)
                .subscribe({
                    next: (updatedReleaseNotes: EMTSoftwareReleaseNotes[]) => {
                        this.dialogRef.close({
                            success: updatedReleaseNotes != null,
                            updatedReleaseNotes: updatedReleaseNotes
                        });
                    },
                    error: (error) => {
                        this.dialogRef.close({
                            success: false,
                            updatedReleaseNotes: null
                        });
                    },
                });
        }
    }

    onCancel(): void {
        this.dialogRef.close({
            canceled: true,
            changedByUser: this.editForm.value.changedByUser
        });
    }
}
